import { Box, Button, Icon, TextField, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { ChangeEvent, useCallback, useContext, useMemo, useState } from 'react';
import { IModalEntity } from '../../../../interfaces/modalEntity';
import { IBaseCertificate, ICertificate, ICertificateUpload } from '../../../../interfaces/certificate';
import { Validator } from '../../../../core/Validator';
import { IDocumentUpload } from '../../../../interfaces/documentUpload';
import { fileToBase64 } from '../../../../utils/fileToBase64';
import { FreelancerContext } from '../FreelancerEntity';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { FreelancerService } from '../../../../services/freelancer';
import { useNotification } from '../../../../+state/useNotification';
import { AxiosError } from 'axios';
import { IErrorResponse } from '../../../../interfaces/errorResponse';
import { ValidationErrorState } from '../../../../types/validationErrorState';
import { Modal } from '../../../common/Modal';
import { ButtonWithLoader } from '../../../common/ButtonWithLoader';
import { TEXTS } from '../../../../core/texts';
import { ColumnBoxFlexFullWidth, ColumnFlexBox, FlexBoxFullWidth } from '../../../common/boxes';
import WithUpload from '../../../common/WithUpload';
import { MAX_RESUME_SIZE, RESUME_ALLOWED_FILE_EXT, RESUME_ALLOWED_FORMATS } from '../../../../core/constraints';
import { getTruncatedName } from '../../../../utils/getTruncatedName';
import IFrame from '../../../common/IFrame';
import ExpandIcon from '../../../common/ExpandIcon';
import { useGetFreelancerCertificate } from '../../../../queryHooks/freelancer/useGetFreelancerCertificate';
import { FREELANCER_ENTITY_QUERY_KEY } from '../../../../queryHooks/freelancer/useGetFreelancerEntity';


const CertificateEntity = ({ onClose, entity }: IModalEntity<ICertificate>) => {
  const theme = useTheme();
  const { user } = useContext(FreelancerContext);
  const { openSuccessToast, openErrorToast } = useNotification();
  const queryClient = useQueryClient();
  const [expandPreview, setExpandPreview] = useState(false);
  const expandPreviewHandler = useCallback(() => setExpandPreview(prev => !prev), []);
  const { data: certificateData } = useGetFreelancerCertificate({ userId: user.id, certId: entity?.id! });

  const mutation = useMutation({
    mutationFn: (cert: ICertificateUpload) => cert.id
      ? FreelancerService.editCertificate(user.id, cert)
      : FreelancerService.uploadCertificate(user.id, cert),
    onSuccess: () => {
      openSuccessToast('Successfully uploaded');
      queryClient.invalidateQueries([FREELANCER_ENTITY_QUERY_KEY]);
      onClose();
    },
    onError: (err: AxiosError<IErrorResponse>) => {
      openErrorToast(err.response?.data.message || err.message);
      onClose();
    }
  });

  const [state, setState] = useState<IBaseCertificate>({
    name: entity?.name || '',
    source: entity?.source || '',
    description: entity?.description || '',
  });
  const [validationError, setValidationError] = useState<ValidationErrorState>({});
  const [file, setFile] = useState<File | null>(null);

  const previewSrc = useMemo(() =>
    file ? URL.createObjectURL(file) : certificateData ? certificateData.src : null, [file, certificateData]);
  const fileName = useMemo(() => file?.name || entity?.document?.fileName || '', [file, entity]);
  const hasChanges = useMemo(() =>
      !entity ||
      (entity.name !== state.name
        || entity.source !== state.source
        || entity.description !== state.description
        || entity?.document?.fileName !== fileName)
    , [entity, fileName, state.name, state.source, state.description]);

  const handleInputChange = useCallback((e: ChangeEvent<HTMLInputElement>) => {
    const name = e.target.name as keyof IBaseCertificate;
    setState(prev => ({ ...prev, [name]: e.target.value }));
  }, []);

  const handleSubmit = useCallback(async () => {
    const errors = Validator.validate({ ...(state as any), fileName }, Validator.NAMES.CERTIFICATE);
    if (errors) {
      return setValidationError(errors);
    }
    const fileContent: string | undefined = file ? (await fileToBase64(file))?.toString() : undefined;
    const fileToUpload: IDocumentUpload | undefined =
      fileContent ? {
        fileName: fileName,
        fileContentBase64: fileContent
      } : undefined;
    const certificate: ICertificateUpload = {
      id: entity?.id,
      name: state.name,
      source: state.source,
      description: state.description,
      ...fileToUpload as IDocumentUpload
    };
    mutation.mutate(certificate);
  }, [state, file, entity, mutation, fileName]);

  return (
    <Modal
      onClose={onClose}
      maxWidth={600}
      title={`${entity ? 'Edit' : 'New'} certificate`}
      headerIcon={<Icon sx={{ color: theme.palette.primary.main }}>grading</Icon>}
      dialogActions={
        <ButtonWithLoader onClick={handleSubmit} sx={{ my: 2, fontSize: { mobile: 18, tablet: 24 } }}
                          disabled={!hasChanges} loading={mutation.isLoading}>
          {'Confirm'}
        </ButtonWithLoader>
      }
    >
      <ColumnBoxFlexFullWidth>
        <TextField variant={'standard'} label={TEXTS.NAME} name={'name'} value={state.name} onChange={handleInputChange}
                   error={!!validationError['name']} helperText={validationError['name']}
        />

        <TextField variant={'standard'} label={TEXTS.SOURCE} name={'source'} value={state.source} sx={{ mt: 2 }}
                   onChange={handleInputChange} error={!!validationError['source']}
                   helperText={validationError['source']}/>

        <TextField variant={'standard'} label={TEXTS.DESCRIPTION} name={'description'} value={state.description}
                   sx={{ mt: 2 }}
                   onChange={handleInputChange} error={!!validationError['description']}
                   helperText={validationError['description']}/>

        <FlexBoxFullWidth mt={4} justifyContent={'space-between'} alignItems={'center'}>
          <WithUpload
            accept={RESUME_ALLOWED_FORMATS}
            handleFileUpload={(file) => setFile(file)}
            maxByteSize={MAX_RESUME_SIZE}
            allowedExtensions={RESUME_ALLOWED_FILE_EXT}
            sx={{ width: 'fit-content' }}
            mr={1}
            error={validationError['fileName']}
          >
            <Button variant={'outlined'}>{TEXTS.ATTACH_FILE}</Button>
          </WithUpload>

          {
            (file || entity?.document?.fileName)
              ? <ColumnFlexBox alignItems={'flex-end'} maxWidth={'60%'}>
                <Typography>
                  {TEXTS.UPLOADED_CERTIFICATE}
                </Typography>
                <Typography variant={'body2'} sx={{ wordBreak: 'break-word' }}>
                  {getTruncatedName(fileName, 100)}
                </Typography>
              </ColumnFlexBox>
              : <Typography>{TEXTS.NOT_UPLOADED}</Typography>
          }
        </FlexBoxFullWidth>
        {
          previewSrc &&
          <ColumnBoxFlexFullWidth>
            <Box alignSelf={'flex-end'} mt={1}>
              <ExpandIcon onClick={expandPreviewHandler} expanded={expandPreview} downDirection={true}
                          tooltip={TEXTS.PREVIEW}/>
            </Box>
            <FlexBoxFullWidth justifyContent={'flex-end'} mt={1} width={'100%'}>
              <Box height={expandPreview ? 300 : 0} width={'100%'} sx={{ transition: 'all .3s ease-in' }}
                   position={'relative'}>
                <IFrame src={previewSrc} title={fileName}/>
              </Box>
            </FlexBoxFullWidth>
          </ColumnBoxFlexFullWidth>
        }
      </ColumnBoxFlexFullWidth>
    </Modal>
  );
};

export default CertificateEntity;